<template>
  <section class="relative my-3 border-t border-b border-gray-300 py-3 text-gray-800" aria-live="polite">
    <div v-if="isWorking" class="relative flex h-10 items-center gap-3 pb-1">
      <span class="skim-icon bg-teal-50 text-teal-700" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 2.75l1.45 4.13a5.8 5.8 0 003.55 3.55L21.25 12 17 13.57a5.8 5.8 0 00-3.55 3.55L12 21.25l-1.45-4.13A5.8 5.8 0 007 13.57L2.75 12 7 10.43a5.8 5.8 0 003.55-3.55L12 2.75z" />
        </svg>
      </span>
      <div class="flex min-w-0 flex-1 flex-col leading-tight">
        <strong class="text-xs text-teal-700">Ceremony Skim</strong>
        <span class="text-xs text-gray-600">{{ skim.message }}</span>
      </div>
      <span class="text-xs text-gray-600">{{ skim.progress }}%</span>
      <span class="absolute right-0 bottom-0 left-0 h-px overflow-hidden bg-gray-200" aria-hidden="true">
        <span class="block h-full bg-teal-600 transition-all" :style="progressStyle"></span>
      </span>
    </div>

    <template v-else-if="isReady">
      <header class="flex items-center gap-3">
        <span class="skim-icon bg-green-50 text-green-700" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M12 2.75l1.45 4.13a5.8 5.8 0 003.55 3.55L21.25 12 17 13.57a5.8 5.8 0 00-3.55 3.55L12 21.25l-1.45-4.13A5.8 5.8 0 007 13.57L2.75 12 7 10.43a5.8 5.8 0 003.55-3.55L12 2.75z" />
          </svg>
        </span>
        <div>
          <p class="m-0 text-xs font-bold uppercase tracking-wider text-teal-700">Ceremony Skim</p>
          <h2 class="m-0 text-base font-semibold leading-tight text-gray-900">{{ headingText }}</h2>
        </div>
        <span
          class="ml-auto inline-flex items-center gap-1 text-xs font-semibold"
          :class="bothBoundariesSelected ? 'text-green-700' : 'text-yellow-700'"
        >
          <svg v-if="bothBoundariesSelected" class="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M3 8.25l3 3L13 4.5" />
          </svg>
          {{ bothBoundariesSelected ? 'Set' : 'Review' }}
        </span>
      </header>

      <p v-if="!bothBoundariesSelected" class="mt-2 mb-0 ml-10 text-xs text-gray-600">
        Choose a start and end to use this clip window.
      </p>

      <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" @keydown.esc="openBoundary = null">
        <div
          v-for="edge in ['start', 'end']"
          :key="edge"
          class="relative"
          :class="`skim-boundary-${edge}`"
        >
          <button
            type="button"
            class="flex h-14 w-full items-center justify-between rounded-lg border bg-white px-3 py-2 text-left transition focus:outline-none focus:ring-2 focus:ring-teal-200"
            :class="hasSelectedBoundary(edge) ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-teal-500'"
            :aria-expanded="String(openBoundary === edge)"
            :aria-controls="`skim-${edge}-picker`"
            @click="toggleBoundary(edge)"
          >
            <span class="flex flex-col">
              <small class="text-xs font-semibold uppercase tracking-wide text-gray-600">{{ boundaryLabel(edge) }}</small>
              <strong class="text-base text-gray-800">{{ formatTimecode(displayBoundarySeconds(edge)) }}</strong>
            </span>
            <svg class="h-4 w-4 text-gray-500" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 6l4 4 4-4" />
            </svg>
          </button>

          <div
            v-if="openBoundary === edge"
            :id="`skim-${edge}-picker`"
            class="skim-boundary-popover rounded-xl border border-gray-300 bg-white p-3"
            role="group"
            :aria-label="`Choose clip ${edge}`"
          >
            <div class="mb-3 flex flex-col">
              <strong class="text-sm capitalize text-gray-800">Choose clip {{ edge }}</strong>
              <span class="text-xs text-gray-600">The sampled frame before and after the detected boundary.</span>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <button
                v-for="option in boundaryOptions(edge)"
                :key="`${edge}-${option.position}`"
                type="button"
                class="overflow-hidden rounded-lg border bg-white text-left focus:outline-none focus:ring-2 focus:ring-teal-200"
                :class="[
                  option.recommended ? 'border-green-300' : 'border-gray-300',
                  isSelectedBoundary(edge, option.seconds) ? 'ring-2 ring-teal-500' : 'hover:border-teal-500',
                ]"
                @click="chooseBoundary(edge, option)"
              >
                <span
                  class="skim-cropped-frame skim-option-frame flex items-end"
                  :style="frameStyle(option.frame)"
                  aria-hidden="true"
                >
                  <span class="m-2 rounded bg-gray-900 bg-opacity-75 px-1 py-px text-xs text-white">{{ formatTimecode(option.seconds) }}</span>
                </span>
                <span class="skim-option-copy flex flex-col gap-1 p-2 text-xs leading-tight text-gray-600">
                  <span class="flex items-center justify-between text-gray-800">
                    <strong>{{ option.position }}</strong>
                    <small v-if="option.recommended" class="rounded-full bg-green-50 px-1 py-px text-xs font-bold uppercase tracking-wide text-green-700">Safer</small>
                  </span>
                  <span>{{ option.description }}</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="cameraAngles.length" class="mt-3">
        <div class="mb-2 flex items-baseline gap-2 text-xs text-gray-600">
          <strong class="text-gray-800">
            {{ cameraAngles.length }} camera {{ cameraAngles.length === 1 ? 'view' : 'views' }}
          </strong>
          <span>{{ analysis.camera_angle_confidence }} confidence</span>
        </div>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div
            v-for="(angle, index) in cameraAngles"
            :key="`${index}-${angle.label}`"
            class="min-w-0 rounded-lg border border-gray-300 bg-white p-1 hover:border-teal-400 hover:bg-gray-50 focus-within:border-teal-400"
          >
            <button
              type="button"
              class="flex w-full min-w-0 items-center gap-2 bg-transparent p-0 text-left focus:outline-none focus:ring-2 focus:ring-teal-200"
              :title="angle.label"
              @click="seekToAngle(angle)"
            >
              <span
                class="skim-cropped-frame skim-camera-image rounded"
                :style="frameStyle(frameForAngle(angle))"
                aria-hidden="true"
              ></span>
              <span class="flex min-w-0 flex-1 flex-col">
                <small class="text-xs font-bold uppercase tracking-wide text-teal-700">View {{ index + 1 }}</small>
                <strong class="skim-camera-label text-xs font-semibold leading-tight text-gray-800">{{ angle.label }}</strong>
              </span>
              <svg class="h-4 w-4 flex-none text-gray-500" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M6 4l4 4-4 4" />
              </svg>
            </button>
            <div v-if="angleEvidenceTimes(angle).length" class="flex items-center gap-1 pt-1 pl-20 text-xs text-gray-500">
              <span>Seen</span>
              <button
                v-for="timestamp in angleEvidenceTimes(angle)"
                :key="timestamp"
                type="button"
                class="rounded bg-gray-100 px-1 py-px text-xs text-teal-800 hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-200"
                @click="seek(timestamp)"
              >
                {{ formatTimecode(timestamp) }}
              </button>
            </div>
          </div>
        </div>
      </div>

    </template>

    <div v-else-if="skim.status === 'not-visible'" class="flex h-8 items-center gap-3 text-xs text-gray-600">
      <span class="skim-icon skim-icon-small bg-gray-100 font-bold text-gray-600" aria-hidden="true">?</span>
      <p class="m-0"><strong class="text-gray-800">Ceremony Skim</strong> could not confidently locate the presentation section. Set the clip window below.</p>
    </div>

    <div v-else-if="skim.status === 'failed'" class="flex h-8 items-center gap-3 text-xs text-gray-600">
      <span class="skim-icon skim-icon-small bg-gray-100 font-bold text-gray-600" aria-hidden="true">!</span>
      <p class="m-0"><strong class="text-red-700">Ceremony Skim</strong> was unavailable. Continue with the clip window below.</p>
    </div>

    <div v-if="frames.length" class="mt-3">
      <div class="mb-2 flex items-baseline justify-between gap-2 text-xs text-gray-600">
        <strong class="text-gray-800">Sampled ceremony</strong>
        <span>Choose a frame to preview</span>
      </div>
      <div class="overflow-x-auto pb-1">
        <div class="skim-filmstrip" :style="filmstripGridStyle">
          <button
            v-for="frame in frames"
            :key="frame.frameNumber"
            type="button"
            class="skim-filmstrip-frame"
            :class="{ 'inside-window': !analysis || frameInsideWindow(frame) }"
            :aria-label="`Preview ${formatTimecode(frame.timestampSeconds)}`"
            :title="formatTimecode(frame.timestampSeconds)"
            @click="seek(frame.timestampSeconds)"
          >
            <span
              class="skim-filmstrip-image"
              :style="frameStyle(frame)"
              aria-hidden="true"
            ></span>
          </button>
          <span class="skim-window-marker start" :style="startMarkerStyle" aria-hidden="true"></span>
          <span class="skim-window-marker end" :style="endMarkerStyle" aria-hidden="true"></span>
        </div>
      </div>
      <div class="mt-1 flex justify-between text-xs text-gray-500" aria-hidden="true">
        <time>{{ formatTimecode(frames[0].timestampSeconds) }}</time>
        <span class="text-yellow-700">{{ filmstripWindowLabel }}</span>
        <time>{{ formatTimecode(frames[frames.length - 1].timestampSeconds) }}</time>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CeremonySkimAssist',
  props: {
    skim: {
      type: Object,
      required: true,
    },
    clipWindow: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      openBoundary: null,
    }
  },
  computed: {
    isWorking() {
      return ['sampling', 'analyzing'].includes(this.skim.status)
    },
    isReady() {
      return this.skim.status === 'ready'
        && this.analysis
        && this.analysis.status === 'suggested'
    },
    analysis() {
      return this.skim.analysis || null
    },
    frames() {
      return Array.isArray(this.skim.frames) ? this.skim.frames : []
    },
    cameraAngles() {
      return this.analysis && Array.isArray(this.analysis.camera_angles)
        ? this.analysis.camera_angles
        : []
    },
    progressStyle() {
      return { width: `${Math.max(0, Math.min(100, this.skim.progress || 0))}%` }
    },
    filmstripGridStyle() {
      return { gridTemplateColumns: `repeat(${this.frames.length}, minmax(0, 1fr))` }
    },
    startMarkerStyle() {
      return this.markerStyle('start')
    },
    endMarkerStyle() {
      return this.markerStyle('end')
    },
    bothBoundariesSelected() {
      return this.clipWindow.startSeconds !== null
        && this.clipWindow.endSeconds !== null
    },
    headingText() {
      return this.bothBoundariesSelected
        ? 'Clip window set'
        : 'Presentation section suggested'
    },
    filmstripWindowLabel() {
      if (!this.analysis) return 'Analysis in progress'
      return this.bothBoundariesSelected ? 'Clip window' : 'Suggested window'
    },
    activeStartSeconds() {
      if (this.clipWindow.startSeconds !== null) return this.clipWindow.startSeconds
      return this.analysis.useful_start.suggested_seconds
    },
    activeEndSeconds() {
      if (this.clipWindow.endSeconds !== null) return this.clipWindow.endSeconds
      return this.analysis.useful_end.suggested_seconds
    },
  },
  methods: {
    formatTimecode(value) {
      const seconds = Math.max(0, Math.round(Number(value || 0)))
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const remaining = seconds % 60
      return [hours, minutes, remaining]
        .map(part => String(part).padStart(2, '0'))
        .join(':')
    },
    toggleBoundary(edge) {
      this.openBoundary = this.openBoundary === edge ? null : edge
    },
    hasSelectedBoundary(edge) {
      return this.clipWindow[`${edge}Seconds`] !== null
    },
    boundaryLabel(edge) {
      return `${this.hasSelectedBoundary(edge) ? 'Clip' : 'Suggested'} ${edge}`
    },
    displayBoundarySeconds(edge) {
      const selected = this.clipWindow[`${edge}Seconds`]
      if (selected !== null) return selected
      const boundary = edge === 'start'
        ? this.analysis.useful_start
        : this.analysis.useful_end
      return boundary.suggested_seconds
    },
    frameByNumber(frameNumber) {
      if (!Number.isInteger(frameNumber) || frameNumber < 1) return null
      return this.frames[frameNumber - 1] || null
    },
    boundaryOptions(edge) {
      if (!this.analysis) return []
      const boundary = edge === 'start'
        ? this.analysis.useful_start
        : this.analysis.useful_end
      const rawOptions = edge === 'start'
        ? [
          {
            position: 'Before',
            description: 'Last sampled frame before presentations',
            seconds: boundary.range_start_seconds,
            frameNumber: boundary.last_non_presentation_frame_number,
            recommended: true,
          },
          {
            position: 'After',
            description: 'First sampled presentation frame',
            seconds: boundary.range_end_seconds,
            frameNumber: boundary.first_presentation_frame_number,
            recommended: false,
          },
        ]
        : [
          {
            position: 'Before',
            description: 'Last sampled presentation frame',
            seconds: boundary.range_start_seconds,
            frameNumber: boundary.last_presentation_frame_number,
            recommended: false,
          },
          {
            position: 'After',
            description: 'First sampled frame after presentations',
            seconds: boundary.range_end_seconds,
            frameNumber: boundary.first_non_presentation_frame_number,
            recommended: true,
          },
        ]
      return rawOptions
        .map(option => ({ ...option, frame: this.frameByNumber(option.frameNumber) }))
        .filter(option => option.frame)
    },
    chooseBoundary(edge, option) {
      this.$emit('select-boundary', { edge, seconds: option.seconds })
      this.openBoundary = null
    },
    isSelectedBoundary(edge, seconds) {
      return this.clipWindow[`${edge}Seconds`] === Math.round(seconds)
    },
    frameStyle(frame) {
      if (!frame) return {}
      const column = frame.cellIndex % 2
      const row = Math.floor(frame.cellIndex / 2)
      return {
        backgroundImage: `url(${frame.dataUrl})`,
        backgroundPosition: `${column * 100}% ${row * 100}%`,
        backgroundSize: '200% 200%',
      }
    },
    frameForAngle(angle) {
      const frameNumber = Array.isArray(angle.frame_numbers)
        ? angle.frame_numbers[0]
        : null
      return this.frameByNumber(frameNumber)
    },
    angleEvidenceTimes(angle) {
      if (!Array.isArray(angle.evidence_timestamps) || !angle.evidence_timestamps.length) {
        return []
      }
      return angle.evidence_timestamps.slice(0, 3)
    },
    seekToAngle(angle) {
      const frame = this.frameForAngle(angle)
      if (frame) this.seek(frame.timestampSeconds)
    },
    seek(seconds) {
      this.$emit('seek', seconds)
    },
    frameInsideWindow(frame) {
      if (!this.analysis) return false
      return frame.timestampSeconds >= this.activeStartSeconds
        && frame.timestampSeconds <= this.activeEndSeconds
    },
    markerStyle(edge) {
      if (!this.frames.length || !this.analysis) return { display: 'none' }
      const seconds = edge === 'start' ? this.activeStartSeconds : this.activeEndSeconds
      let boundaryIndex
      if (edge === 'start') {
        const firstInsideIndex = this.frames.findIndex(
          frame => frame.timestampSeconds >= seconds,
        )
        boundaryIndex = firstInsideIndex === -1
          ? this.frames.length
          : firstInsideIndex
      } else {
        let lastInsideIndex = -1
        this.frames.forEach((frame, index) => {
          if (frame.timestampSeconds <= seconds) lastInsideIndex = index
        })
        boundaryIndex = lastInsideIndex + 1
      }
      const percent = Math.max(
        0,
        Math.min(100, (boundaryIndex / this.frames.length) * 100),
      )
      return {
        left: `${percent}%`,
        transform: boundaryIndex > 0
          && (edge === 'end' || boundaryIndex === this.frames.length)
          ? 'translateX(-100%)'
          : undefined,
      }
    },
  },
}
</script>

<style scoped>
.skim-icon {
  display: inline-grid;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  place-items: center;
}

.skim-icon-small {
  width: 26px;
  height: 26px;
}

.skim-icon svg {
  width: 17px;
  height: 17px;
}

header svg,
button > svg,
.skim-icon svg {
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.7;
}

.skim-boundary-popover {
  position: absolute;
  z-index: 30;
  top: calc(100% + 7px);
  left: 0;
  width: min(430px, calc(100vw - 44px));
  box-shadow: 0 14px 34px rgba(25, 39, 51, 0.18);
}

.skim-boundary-end .skim-boundary-popover {
  right: 0;
  left: auto;
}

.skim-cropped-frame {
  background-color: #111827;
  background-repeat: no-repeat;
}

.skim-option-frame {
  width: 100%;
  aspect-ratio: 16 / 9;
}

.skim-option-copy {
  min-height: 56px;
}

.skim-camera-image {
  flex: 0 0 68px;
  width: 68px;
  aspect-ratio: 16 / 9;
}

.skim-camera-label {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.skim-filmstrip {
  display: grid;
  position: relative;
  min-width: 620px;
  height: 44px;
  overflow: hidden;
  border: 1px solid #24313a;
  border-radius: 7px;
  background: #111820;
}

.skim-filmstrip-frame {
  position: relative;
  overflow: hidden;
  min-width: 0;
  padding: 0;
  border: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.13);
  background-color: #111820;
  background-repeat: no-repeat;
  opacity: 0.34;
}

.skim-filmstrip-image {
  position: absolute;
  top: 0;
  left: 50%;
  display: block;
  width: auto;
  height: 100%;
  aspect-ratio: 16 / 9;
  transform: translateX(-50%);
  background-color: #111820;
  background-repeat: no-repeat;
}

.skim-filmstrip-frame.inside-window {
  opacity: 0.95;
}

.skim-filmstrip-frame:hover,
.skim-filmstrip-frame:focus-visible {
  z-index: 2;
  opacity: 1;
  outline: 2px solid #fff;
  outline-offset: -2px;
}

.skim-window-marker {
  position: absolute;
  z-index: 3;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #f4b628;
  box-shadow: 0 0 0 1px rgba(30, 38, 44, 0.42);
  pointer-events: none;
}

.skim-window-marker.start::before,
.skim-window-marker.end::before {
  position: absolute;
  top: 2px;
  padding: 1px 3px;
  border-radius: 3px;
  background: #f4b628;
  color: #27313a;
  font-size: 0.5rem;
  font-weight: 800;
}

.skim-window-marker.start::before {
  left: 2px;
  content: 'IN';
}

.skim-window-marker.end::before {
  right: 2px;
  content: 'OUT';
}

@media (max-width: 640px) {
  .skim-boundary-end .skim-boundary-popover {
    right: auto;
    left: 0;
  }

  .skim-boundary-popover {
    width: min(430px, calc(100vw - 32px));
  }

  .skim-filmstrip {
    min-width: 560px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .transition,
  .transition-all {
    transition: none;
  }
}
</style>
